import {
  Checkbox,
  ChoiceRow,
  Field,
  FieldGrid,
  FormBlock,
  Input,
  Radio,
  RadioGroup,
  Select,
  Textarea,
  formatCPF,
  titleCase,
} from '@westhetics/ui'
import { TextField } from '@/features/clinic-profile/blocks'
import { onlyDigits } from '@/features/clinic-profile/doc-number'
import { formatPostalCode, maskPhone } from '@/features/clinic-profile/form'
import { STATES } from '@/features/clinic-profile/labels'
import type { FormController } from '@/features/clinic-profile/use-form-state'
import { errorMessage } from '@/lib/errors'
import {
  useLookupPostalCode,
  type Audience,
  type ForeignDocumentType,
  type Gender,
  type ImageConsent,
  type MaritalStatus,
  type ReferralChannel,
} from './api'
import { maskDate, maskDocNumber, maskInstagram, maskWhatsapp, type PatientFormState } from './form'
import {
  AUDIENCES,
  COUNTRIES,
  DEFAULT_DIAL_CODE,
  DIAL_CODES,
  FOREIGN_DOCUMENT_TYPES,
  FOREIGN_DOCUMENT_TYPE_LABELS,
  GENDERS,
  GENDER_LABELS,
  IMAGE_CONSENTS,
  IMAGE_CONSENT_LABELS,
  MARITAL_STATUSES,
  MARITAL_STATUS_LABELS,
  REFERRAL_CHANNELS,
  REFERRAL_CHANNEL_LABELS,
  personWords,
} from './labels'

/**
 * Os blocos do cadastro de paciente (docs/areas/pacientes.md, "Dados pessoais"), cada um com a
 * linha que diz onde o dado aparece depois. São os mesmos em "Criar paciente" e na aba Dados
 * pessoais da ficha. Só o opcional recebe "· opcional"; o que falta do resto não impede salvar e
 * aparece no medidor (RN-PAC-09).
 */

type PatientForm = FormController<PatientFormState>

interface BlockProps {
  form: PatientForm
  /** Cadastro arquivado ou de clínica irmã: lê-se, não se edita. */
  readOnly?: boolean
}

/** O campo de nome de pessoa: ao sair, fica em caixa de título, como vai para o contrato. */
export function NameField({
  form,
  name,
  label,
  hint,
  readOnly,
  className,
}: BlockProps & {
  name: 'name' | 'guardianName'
  label: string
  hint?: string
  className?: string
}) {
  const value = form.values[name]
  const set = (text: string) =>
    form.update(name === 'name' ? { name: text } : { guardianName: text })
  return (
    <Field label={label} error={form.errors[name]} hint={hint} className={className}>
      <Input
        value={value}
        maxLength={120}
        autoComplete="off"
        readOnly={readOnly}
        onChange={(event) => set(event.target.value)}
        onBlur={() => {
          if (value.trim() !== '') {
            const cased = titleCase(value)
            if (cased !== value) set(cased)
          }
          form.touch(name)
        }}
      />
    </Field>
  )
}

/**
 * O público da pessoa (RN-PAC-36): só na clínica com mais de um público, com os que ela contratou.
 * Depois do primeiro documento o público não muda (RN-PAC-37), e o campo fica só para leitura.
 */
function AudienceField({
  form,
  audiences,
  isLocked,
  readOnly,
}: BlockProps & { audiences: Audience[]; isLocked: boolean }) {
  const value = form.values.audience
  const options = AUDIENCES.filter((audience) => audience === value || audiences.includes(audience))
  return (
    <Field
      label="Público"
      error={form.errors.audience}
      hint={
        form.errors.audience
          ? undefined
          : isLocked
            ? 'Este cadastro já tem documentos: o público não muda. Para outro público, crie outro cadastro.'
            : 'Decide quais documentos avulsos a ficha oferece. Aluno, funcionário e terceiro podem ter CNPJ.'
      }
      className="col-span-full"
    >
      <Select
        disabled={readOnly || isLocked}
        value={value}
        onChange={(event) => form.update({ audience: event.target.value as Audience })}
        onBlur={() => form.touch('audience')}
      >
        {options.map((audience) => (
          <option key={audience} value={audience}>
            {personWords(audience).title}
          </option>
        ))}
      </Select>
    </Field>
  )
}

export function IdentificationBlock({
  form,
  readOnly,
  audiences = [],
  isAudienceLocked = false,
}: BlockProps & {
  /** Os públicos que a clínica contratou (`clinics.audiences`); com um só, o campo não aparece. */
  audiences?: Audience[]
  /** O cadastro já tem documento: o público não muda (RN-PAC-37). */
  isAudienceLocked?: boolean
}) {
  const { values } = form
  const words = personWords(values.audience)
  const showAudience = audiences.length > 1 || values.audience !== 'patient'
  return (
    <FormBlock
      title="Identificação"
      note={
        values.audience === 'patient'
          ? 'Entra na qualificação das partes do contrato e chega preenchida na anamnese.'
          : 'Entra na qualificação das partes do contrato.'
      }
    >
      <FieldGrid>
        {showAudience ? (
          <AudienceField
            form={form}
            audiences={audiences}
            isLocked={isAudienceLocked}
            readOnly={readOnly}
          />
        ) : null}
        <NameField
          form={form}
          name="name"
          label="Nome completo"
          className="col-span-full"
          readOnly={readOnly}
          hint={
            form.errors.name
              ? undefined
              : 'Sai no contrato em caixa de título, mesmo digitado em minúsculas.'
          }
        />
        <TextField
          form={form}
          name="birthDate"
          label="Data de nascimento"
          inputMode="numeric"
          placeholder="dd/mm/aaaa"
          mask={maskDate}
          readOnly={readOnly}
        />
        {values.isForeign ? (
          <>
            <Field label="Tipo de documento" error={form.errors.foreignDocumentType}>
              <Select
                placeholder="Selecione"
                disabled={readOnly}
                value={values.foreignDocumentType}
                onChange={(event) =>
                  form.update({
                    foreignDocumentType: event.target.value as ForeignDocumentType | '',
                  })
                }
                onBlur={() => form.touch('foreignDocumentType')}
              >
                {FOREIGN_DOCUMENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {FOREIGN_DOCUMENT_TYPE_LABELS[type]}
                  </option>
                ))}
              </Select>
            </Field>
            <TextField
              form={form}
              name="foreignDocumentNumber"
              label="Número do documento"
              maxLength={20}
              autoCapitalize="characters"
              readOnly={readOnly}
            />
            <Field label="País emissor" error={form.errors.foreignDocumentCountry}>
              <Select
                placeholder="Selecione"
                disabled={readOnly}
                value={values.foreignDocumentCountry}
                onChange={(event) => form.update({ foreignDocumentCountry: event.target.value })}
                onBlur={() => form.touch('foreignDocumentCountry')}
              >
                {COUNTRIES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        ) : (
          <TextField
            form={form}
            name="docNumber"
            label={words.document}
            data-field="docNumber"
            inputMode={values.audience === 'patient' ? 'numeric' : 'text'}
            placeholder={values.audience === 'patient' ? '000.000.000-00' : 'CPF ou CNPJ'}
            mask={(text) => maskDocNumber(text, values.audience)}
            readOnly={readOnly}
          />
        )}
        <TextField form={form} name="rg" label="RG" optional maxLength={24} readOnly={readOnly} />
        <ChoiceRow
          className="col-span-full"
          control={
            <Checkbox
              checked={values.isForeign}
              disabled={readOnly}
              onCheckedChange={(checked) => form.update({ isForeign: checked === true })}
            />
          }
          description="Passaporte, RNE ou CRNM entram no lugar do CPF no cadastro e no contrato."
        >
          {values.audience === 'patient' ? 'Estrangeira sem CPF' : 'Estrangeiro sem CPF nem CNPJ'}
        </ChoiceRow>
      </FieldGrid>
    </FormBlock>
  )
}

export function CivilBlock({ form, readOnly }: BlockProps) {
  const { values } = form
  return (
    <FormBlock
      title="Estado civil e profissão"
      note="Completam a qualificação da contratante no contrato."
    >
      <FieldGrid>
        <Field label="Estado civil" error={form.errors.maritalStatus}>
          <Select
            placeholder="Selecione"
            disabled={readOnly}
            value={values.maritalStatus}
            onChange={(event) =>
              form.update({ maritalStatus: event.target.value as MaritalStatus | '' })
            }
            onBlur={() => form.touch('maritalStatus')}
          >
            {MARITAL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {MARITAL_STATUS_LABELS[status]}
              </option>
            ))}
          </Select>
        </Field>
        <TextField
          form={form}
          name="occupation"
          label="Profissão"
          optional
          maxLength={80}
          readOnly={readOnly}
        />
        <Field
          label="Gênero"
          optional={!readOnly}
          error={form.errors.gender}
          hint={'Só ajusta a concordância do contrato ("a contratante").'}
        >
          <Select
            disabled={readOnly}
            value={values.gender}
            onChange={(event) => form.update({ gender: event.target.value as Gender })}
          >
            {GENDERS.map((gender) => (
              <option key={gender} value={gender}>
                {GENDER_LABELS[gender]}
              </option>
            ))}
          </Select>
        </Field>
      </FieldGrid>
    </FormBlock>
  )
}

/** WhatsApp com o DDI ao lado: o Brasil é o padrão e tem máscara de DDD e número. */
export function WhatsappFields({ form, readOnly }: BlockProps) {
  const { values } = form
  return (
    <>
      <Field
        label="WhatsApp"
        error={form.errors.whatsapp}
        hint={form.errors.whatsapp ? undefined : 'É por onde seguem a anamnese e os documentos.'}
      >
        <Input
          inputMode="tel"
          autoComplete="off"
          placeholder={
            values.dialCode === DEFAULT_DIAL_CODE ? '67 99686-0918' : 'Número com código de área'
          }
          readOnly={readOnly}
          value={values.whatsapp}
          onChange={(event) =>
            form.update({ whatsapp: maskWhatsapp(event.target.value, values.dialCode) })
          }
          onBlur={() => form.touch('whatsapp')}
        />
      </Field>
      <Field label="País do número" error={form.errors.dialCode}>
        <Select
          disabled={readOnly}
          value={values.dialCode}
          onChange={(event) =>
            form.update({
              dialCode: event.target.value,
              whatsapp: maskWhatsapp(values.whatsapp, event.target.value),
            })
          }
        >
          {DIAL_CODES.map((item) => (
            <option key={item.code} value={item.code}>
              {item.label}
            </option>
          ))}
        </Select>
      </Field>
    </>
  )
}

export function ContactBlock({
  form,
  readOnly,
  emailLock,
}: BlockProps & {
  /** O e-mail que só entra pelo código (RN-PAC-58): travado, com a frase do porquê. */
  emailLock?: { hint: string }
}) {
  return (
    <FormBlock
      title="Contato"
      note="O WhatsApp é o canal de envio. O e-mail recebe a cópia de cada documento assinado."
    >
      <FieldGrid>
        <WhatsappFields form={form} readOnly={readOnly} />
        <TextField
          form={form}
          name="email"
          label="E-mail"
          type="email"
          inputMode="email"
          autoCapitalize="none"
          placeholder="nome@email.com.br"
          readOnly={readOnly || Boolean(emailLock)}
          hint={emailLock?.hint}
        />
        <TextField
          form={form}
          name="instagram"
          label="Instagram"
          optional
          autoCapitalize="none"
          placeholder="nome.de.usuario"
          mask={maskInstagram}
          readOnly={readOnly}
          hint="Só o nome de usuário, sem @. Não entra em documento."
        />
      </FieldGrid>
    </FormBlock>
  )
}

/** Endereço com o CEP que preenche o resto pela API, sem mexer em número e complemento (RN-PAC-08). */
export function AddressBlock({ form, readOnly }: BlockProps) {
  const { values } = form
  const lookup = useLookupPostalCode()
  const isBrazil = values.country === 'BR'

  function changePostalCode(text: string) {
    if (!isBrazil) {
      form.update({ postalCode: text.slice(0, 20) })
      return
    }
    const postalCode = formatPostalCode(text)
    const digits = onlyDigits(postalCode)
    const changed = digits !== onlyDigits(values.postalCode)
    form.update({ postalCode })
    if (!changed) return
    if (digits.length < 8) {
      lookup.reset()
      return
    }
    lookup.mutate(digits, {
      // O que a consulta não tem (CEP geral, sem rua nem bairro) fica como está.
      onSuccess: (address) =>
        form.update({
          city: address.city,
          state: address.state,
          ...(address.street ? { street: address.street } : {}),
          ...(address.district ? { district: address.district } : {}),
        }),
    })
  }

  // A consulta é um atalho: a falha vira dica no campo e o endereço segue à mão.
  const postalCodeHint = lookup.isPending
    ? 'Buscando o endereço…'
    : lookup.isError
      ? errorMessage(lookup.error)
      : undefined

  return (
    <FormBlock title="Endereço" note="Entra na qualificação da contratante no contrato.">
      <FieldGrid>
        <Field
          label={isBrazil ? 'CEP' : 'Código postal'}
          error={form.errors.postalCode}
          hint={form.errors.postalCode ? undefined : postalCodeHint}
        >
          <Input
            inputMode={isBrazil ? 'numeric' : 'text'}
            autoComplete="off"
            placeholder={isBrazil ? '00000-000' : undefined}
            readOnly={readOnly}
            value={values.postalCode}
            onChange={(event) => changePostalCode(event.target.value)}
            onBlur={() => form.touch('postalCode')}
          />
        </Field>
        <TextField form={form} name="street" label="Rua" maxLength={120} readOnly={readOnly} />
        <TextField
          form={form}
          name="number"
          label="Número"
          maxLength={10}
          readOnly={readOnly}
          hint={form.errors.number ? undefined : 'Sem número, escreva s/n.'}
        />
        <TextField
          form={form}
          name="complement"
          label="Complemento"
          optional
          maxLength={60}
          readOnly={readOnly}
        />
        <TextField form={form} name="district" label="Bairro" maxLength={80} readOnly={readOnly} />
        <TextField form={form} name="city" label="Cidade" maxLength={80} readOnly={readOnly} />
        {isBrazil ? (
          <Field label="UF" error={form.errors.state}>
            <Select
              placeholder="Selecione"
              disabled={readOnly}
              value={values.state}
              onChange={(event) => form.update({ state: event.target.value })}
              onBlur={() => form.touch('state')}
            >
              {STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <TextField
            form={form}
            name="state"
            label="Estado ou província"
            maxLength={60}
            readOnly={readOnly}
          />
        )}
        <Field label="País" optional={!readOnly} error={form.errors.country}>
          <Select
            disabled={readOnly}
            value={values.country}
            onChange={(event) => {
              lookup.reset()
              // Fora do Brasil, CEP e UF viram texto livre: o que estava lá não serve mais.
              form.update({ country: event.target.value, postalCode: '', state: '' })
            }}
          >
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </Select>
        </Field>
      </FieldGrid>
    </FormBlock>
  )
}

export function GuardianBlock({
  form,
  readOnly,
  isMinor,
}: BlockProps & {
  /** Paciente menor de idade: o responsável deixa de ser opcional (RT-18). */
  isMinor: boolean
}) {
  return (
    <FormBlock
      title={
        <>
          Responsável
          {isMinor ? null : (
            <span className="text-label-soft tracking-normal normal-case"> · opcional</span>
          )}
        </>
      }
      note={
        isMinor
          ? 'A paciente é menor de idade: é o responsável quem assina os documentos.'
          : 'Exigido quando a paciente é menor de idade: é quem assina os documentos por ela.'
      }
    >
      <FieldGrid>
        <NameField
          form={form}
          name="guardianName"
          label="Nome do responsável"
          className="col-span-full"
          readOnly={readOnly}
        />
        <TextField
          form={form}
          name="guardianDocNumber"
          label="CPF do responsável"
          inputMode="numeric"
          placeholder="000.000.000-00"
          mask={formatCPF}
          readOnly={readOnly}
        />
        <TextField
          form={form}
          name="guardianWhatsapp"
          label="WhatsApp do responsável"
          inputMode="tel"
          placeholder="67 99686-0918"
          mask={maskPhone}
          readOnly={readOnly}
        />
      </FieldGrid>
    </FormBlock>
  )
}

/**
 * O uso de imagem é o dado à clínica do cadastro, nunca à pessoa (RN-PAC-43): no cadastro de clínica irmã da rede,
 * a nota diz de quem é.
 */
export function ConsentBlock({
  form,
  readOnly,
  sourceClinicName,
}: BlockProps & { sourceClinicName?: string }) {
  return (
    <FormBlock
      title="Consentimentos"
      note={
        sourceClinicName
          ? `Autorização dada a ${sourceClinicName}: vale só para ela, nunca para esta clínica.`
          : 'Vale só para esta clínica. A resposta dada ao gerar documentos atualiza este campo.'
      }
    >
      <RadioGroup
        aria-label="Autorização de uso de imagem"
        className="tablet:grid-cols-3"
        disabled={readOnly}
        value={form.values.imageConsent}
        onValueChange={(value) => form.update({ imageConsent: value as ImageConsent })}
      >
        {IMAGE_CONSENTS.map((consent) => (
          <ChoiceRow key={consent} control={<Radio value={consent} />}>
            {IMAGE_CONSENT_LABELS[consent]}
          </ChoiceRow>
        ))}
      </RadioGroup>
    </FormBlock>
  )
}

export function OriginBlock({ form, readOnly }: BlockProps) {
  const { values } = form
  return (
    <FormBlock
      title="Origem e observações"
      note="Ficam na clínica: nunca entram em documento, anamnese ou exportação."
    >
      <FieldGrid>
        <Field
          label="Como chegou à clínica"
          optional={!readOnly}
          error={form.errors.referralChannel}
        >
          <Select
            placeholder="Não informado"
            disabled={readOnly}
            value={values.referralChannel}
            onChange={(event) =>
              form.update({ referralChannel: event.target.value as ReferralChannel | '' })
            }
            onBlur={() => form.touch('referralChannel')}
          >
            {REFERRAL_CHANNELS.map((channel) => (
              <option key={channel} value={channel}>
                {REFERRAL_CHANNEL_LABELS[channel]}
              </option>
            ))}
          </Select>
        </Field>
        <TextField
          form={form}
          name="referralDetail"
          label="Detalhe da origem"
          optional
          maxLength={120}
          readOnly={readOnly}
          hint="Quem indicou, qual anúncio, qual evento."
        />
        <Field
          className="col-span-full"
          label="Observações internas"
          optional={!readOnly}
          error={form.errors.notes}
          meta={`${values.notes.length}/2000`}
        >
          <Textarea
            rows={4}
            maxLength={2000}
            readOnly={readOnly}
            value={values.notes}
            onChange={(event) => form.update({ notes: event.target.value })}
            onBlur={() => form.touch('notes')}
          />
        </Field>
      </FieldGrid>
    </FormBlock>
  )
}
